import { Checkbox, FormControlLabel, Stack, TextField, TextFieldProps } from "@mui/material";
import { SuggestedStatePicker } from "components/ArtificialIntelligence/SuggestedStatePicker";
import { SuggestedTextField } from "components/ArtificialIntelligence/SuggestedTextField";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";

import type { ManifestFormValues } from "../types";
import type { ReviewFieldDiff } from "./reviewFieldDiffs";

import { reviewDiffAdornment } from "./reviewDiffAdornment";

type GeneratorSiteAddressFieldsProps = {
  control: Control<ManifestFormValues>;
  setValue: UseFormSetValue<ManifestFormValues>;
  needsReview: boolean;
  /** True when the loaded manifest had no site address (all generatorLocation* blank). */
  siteInitiallyBlank: boolean;
  /** True when the mailing block has a value to copy from for display. */
  mailingPopulated: boolean;
  /** Review-time diffs keyed by review form field name (empty when none). */
  fieldDiffs?: Map<string, ReviewFieldDiff>;
};

// The site address fields, all string-valued — typed explicitly (no `as const`)
// so they're valid setValue paths without a const assertion.
type GeneratorSiteFieldName = Extract<keyof ManifestFormValues, `generatorLocation${string}`>;

const SITE_FIELDS: GeneratorSiteFieldName[] = [
  "generatorLocationStreet1",
  "generatorLocationStreet2",
  "generatorLocationCity",
  "generatorLocationState",
  "generatorLocationZip",
];

/**
 * Generator site address (EPA Form 8700-22 Box 5). The form prescribes leaving
 * the site address blank when it equals the mailing address ("if different than
 * mailing"), so a blank here reads as "missing data" in review. When the site
 * block came in blank and a mailing address exists, we offer a "Same address"
 * checkbox: the reviewer confirms it (defaults unchecked, because a blank can
 * also be an extraction miss), and the site inputs then show the mailing
 * address, disabled.
 *
 * The confirmation is persisted via the `generatorSiteAddressSameAsMailing` form
 * field, so it survives reopening the review. The shown values are display-only
 * and the site address is never written: confirming clears any typed site value,
 * so a save records the site address blank — exactly what the manifest carried.
 */
export function GeneratorSiteAddressFields({
  control,
  setValue,
  needsReview,
  siteInitiallyBlank,
  mailingPopulated,
  fieldDiffs,
}: GeneratorSiteAddressFieldsProps) {
  const sameAsMailing = useWatch({ control, name: "generatorSiteAddressSameAsMailing" });
  const diff = (field: string): ReviewFieldDiff | undefined => fieldDiffs?.get(field);

  const handleToggle = (checked: boolean) => {
    setValue("generatorSiteAddressSameAsMailing", checked, { shouldDirty: true });
    // Confirming "same as mailing" means the site address is blank. Clear any
    // value the reviewer typed before checking so it isn't silently persisted.
    if (checked) {
      for (const field of SITE_FIELDS) setValue(field, "", { shouldDirty: true });
    }
  };

  const editableFields = (
    <>
      <SuggestedTextField
        name="generatorLocationStreet1"
        control={control}
        needsReview={needsReview}
        label="Site Street"
        size="small"
        fullWidth
        slotProps={{
          input: { endAdornment: reviewDiffAdornment(diff("generatorLocationStreet1")) },
        }}
      />
      <SuggestedTextField
        name="generatorLocationStreet2"
        control={control}
        needsReview={needsReview}
        label="Site Street 2"
        size="small"
        fullWidth
        slotProps={{
          input: { endAdornment: reviewDiffAdornment(diff("generatorLocationStreet2")) },
        }}
      />
      <Stack direction="row" spacing={2}>
        <SuggestedTextField
          name="generatorLocationCity"
          control={control}
          needsReview={needsReview}
          label="City"
          size="small"
          sx={{ flex: 2, minWidth: 0 }}
          slotProps={{
            input: { endAdornment: reviewDiffAdornment(diff("generatorLocationCity")) },
          }}
        />
        <SuggestedStatePicker
          name="generatorLocationState"
          control={control}
          needsReview={needsReview}
          sx={{ flex: 1, minWidth: 56 }}
          endAdornment={reviewDiffAdornment(diff("generatorLocationState"))}
        />
        <SuggestedTextField
          name="generatorLocationZip"
          control={control}
          needsReview={needsReview}
          label="Zip"
          size="small"
          sx={{ flex: 1.5, minWidth: 0 }}
          slotProps={{ input: { endAdornment: reviewDiffAdornment(diff("generatorLocationZip")) } }}
        />
      </Stack>
    </>
  );

  // Only offer the affordance when the site block is blank and there is a
  // mailing address to mirror. Both blank == genuinely missing — keep the
  // editable inputs so the reviewer can supply the real address.
  if (!siteInitiallyBlank || !mailingPopulated) {
    return editableFields;
  }

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!sameAsMailing}
            onChange={(event) => handleToggle(event.target.checked)}
            size="small"
          />
        }
        label="Same address"
      />
      {sameAsMailing ? <SameAsMailingDisplay control={control} /> : editableFields}
    </>
  );
}

/**
 * Disabled, display-only mirror of the mailing address shown under the Site
 * address heading. Reads the live mailing values so edits to the mailing block
 * stay reflected, but is not bound to the site form fields — nothing here is
 * persisted. Field labels match the editable inputs so checking the box doesn't
 * change any label.
 */
function SameAsMailingDisplay({ control }: { control: Control<ManifestFormValues> }) {
  const [street1, street2, city, state, zip] = useWatch({
    control,
    name: [
      "generatorMailStreet1",
      "generatorMailStreet2",
      "generatorMailCity",
      "generatorMailState",
      "generatorMailZip",
    ],
  });

  // Explicitly typed (no `as const`) so `size` stays a valid TextField literal.
  const displayProps: Pick<TextFieldProps, "size" | "disabled" | "slotProps"> = {
    size: "small",
    disabled: true,
    slotProps: { input: { readOnly: true } },
  };

  return (
    <>
      <TextField {...displayProps} label="Site Street" value={street1 ?? ""} fullWidth />
      <TextField {...displayProps} label="Site Street 2" value={street2 ?? ""} fullWidth />
      <Stack direction="row" spacing={2}>
        <TextField
          {...displayProps}
          label="City"
          value={city ?? ""}
          sx={{ flex: 2, minWidth: 0 }}
        />
        <TextField
          {...displayProps}
          label="State/Territory"
          value={state ?? ""}
          sx={{ flex: 1, minWidth: 56 }}
        />
        <TextField
          {...displayProps}
          label="Zip"
          value={zip ?? ""}
          sx={{ flex: 1.5, minWidth: 0 }}
        />
      </Stack>
    </>
  );
}
